import * as React from "react";
import {
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Paper,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import {
  ResponsiveContainer,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Bar,
  Line,
} from "recharts";
import { useDataProvider } from "react-admin";
import { DatePicker } from "@mui/x-date-pickers";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

interface SiteSortingRatePanelProps {
  siteId: string;
  inviteToken?: string;
}

type TimelinePoint = {
  period: string;
  totalTonnage: number;
  sortedTonnage: number;
  nonSortedTonnage: number;
  ratePct: number;
};

export const SiteSortingRatePanel: React.FC<SiteSortingRatePanelProps> = ({
  siteId,
  inviteToken,
}) => {
  const dataProvider = useDataProvider() as any;

  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  const [rows, setRows] = React.useState<TimelinePoint[]>([]);
  const [kpis, setKpis] = React.useState<{
    lastMonthRate: number;
    avg3Rate: number;
    ytdRate: number;
  } | null>(null);

  const [fromMonth, setFromMonth] = React.useState<Dayjs>(
    dayjs().subtract(11, "month").startOf("month")
  );
  const [toMonth, setToMonth] = React.useState<Dayjs>(dayjs().endOf("month"));

  React.useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setErr(null);

      try {
        const opts = { inviteToken: "", from: "", to: "" };
        if (inviteToken) {
          opts.inviteToken = inviteToken;
        }
        if (fromMonth) {
          opts.from = fromMonth.format("YYYY-MM");
        }
        if (toMonth) {
          opts.to = toMonth.format("YYYY-MM");
        }

        let resp;
        if (!inviteToken) {
          resp = await dataProvider.getSiteSortingRate(siteId, opts);
        } else {
          const qsParts: string[] = [];
          if (opts?.from) qsParts.push(`from=${encodeURIComponent(opts.from)}`);
          if (opts?.to) qsParts.push(`to=${encodeURIComponent(opts.to)}`);
          if (opts?.inviteToken)
            qsParts.push(`inviteToken=${encodeURIComponent(opts.inviteToken)}`);

          const qsStr = qsParts.length ? `?${qsParts.join("&")}` : "";
          const res = await fetch(
            `${API_URL}/sites/${siteId}/sorting-rate${qsStr}`
          );
          resp = await res.json();
          if (!res.ok)
            throw new Error(resp?.message || "Erreur API taux de tri");
        }

        if (!alive) return;
        const tl = resp.data?.timeline ?? [];

        const mapped: TimelinePoint[] = tl.map((d: any) => {
          const total = d.totalTonnage || 0;
          const sorted = d.sortedTonnage || 0;
          const nonSorted = Math.max(total - sorted, 0);
          return {
            period: d.period,
            totalTonnage: total,
            sortedTonnage: sorted,
            nonSortedTonnage: nonSorted,
            ratePct: (d.rate || 0) * 100,
          };
        });

        setRows(mapped);

        setKpis({
          lastMonthRate: (resp.data?.kpis?.lastMonthRate || 0) * 100,
          avg3Rate: (resp.data?.kpis?.avg3Rate || 0) * 100,
          ytdRate: (resp.data?.kpis?.ytdRate || 0) * 100,
        });
      } catch (e: any) {
        if (!alive) return;
        setErr(e.message || "Impossible de charger le taux de tri");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [siteId, dataProvider, inviteToken, fromMonth, toMonth]);

  return (
    <Card variant="outlined" sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Taux de tri (mensuel)
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <DatePicker
              label="De (mois)"
              views={["year", "month"]}
              openTo="month"
              value={fromMonth}
              onChange={(v) => {
                if (!v) return;
                setFromMonth(v.startOf("month"));
                if (v.isAfter(toMonth)) {
                  setToMonth(v.endOf("month"));
                }
              }}
              slotProps={{
                textField: { fullWidth: true, size: "small" },
              }}
              maxDate={toMonth}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <DatePicker
              label="À (mois)"
              views={["year", "month"]}
              openTo="month"
              value={toMonth}
              onChange={(v) => {
                if (!v) return;
                setToMonth(v.endOf("month"));
              }}
              slotProps={{
                textField: { fullWidth: true, size: "small" },
              }}
              minDate={fromMonth}
            />
          </Grid>
        </Grid>

        {loading ? (
          <CircularProgress />
        ) : err ? (
          <Alert severity="error">{err}</Alert>
        ) : rows.length === 0 ? (
          <Alert severity="info">Aucune donnée de tri pour ce site.</Alert>
        ) : (
          <>
            {kpis && (
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, textAlign: "center", bgcolor: "#fafafa" }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Dernier mois
                    </Typography>
                    <Typography variant="h5" fontWeight={600}>
                      {kpis.lastMonthRate.toFixed(1)}%
                    </Typography>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, textAlign: "center", bgcolor: "#fafafa" }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Moyenne 3 derniers mois
                    </Typography>
                    <Typography variant="h5" fontWeight={600}>
                      {kpis.avg3Rate.toFixed(1)}%
                    </Typography>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, textAlign: "center", bgcolor: "#fafafa" }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Taux YTD
                    </Typography>
                    <Typography variant="h5" fontWeight={600}>
                      {kpis.ytdRate.toFixed(1)}%
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            )}

            <Box sx={{ width: "100%", height: 360 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={rows}
                  margin={{ top: 10, right: 30, bottom: 10, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="period"
                    tickFormatter={(p) => {
                      const [y, m] = String(p).split("-");
                      const d = dayjs(`${y}-${m}-01`);
                      return d.format("MMM YY");
                    }}
                  />

                  {/* Axe gauche = tonnage */}
                  <YAxis
                    yAxisId="tonnage"
                    orientation="left"
                    allowDecimals={false}
                    label={{
                      value: "Tonnage (t)",
                      angle: -90,
                      position: "insideLeft",
                    }}
                  />

                  {/* Axe droite = taux (%) */}
                  <YAxis
                    yAxisId="rate"
                    orientation="right"
                    allowDecimals
                    domain={[0, 100]}
                    label={{
                      value: "Taux de tri (%)",
                      angle: 90,
                      position: "insideRight",
                    }}
                  />

                  <Tooltip
                    formatter={(val: any, name: string) => {
                      if (name === "Taux de tri (%)") {
                        return [`${Number(val).toFixed(1)} %`, name];
                      }
                      return [Number(val).toFixed(2), name];
                    }}
                    labelFormatter={(p) => {
                      const [y, m] = String(p).split("-");
                      const d = dayjs(`${y}-${m}-01`);
                      return d.format("MMMM YYYY");
                    }}
                  />
                  <Legend />

                  {/* Barres empilées trié / non trié */}
                  <Bar
                    yAxisId="tonnage"
                    dataKey="sortedTonnage"
                    name="Trié (t)"
                    stackId="tonnage"
                    fill="#4caf50"
                  />
                  <Bar
                    yAxisId="tonnage"
                    dataKey="nonSortedTonnage"
                    name="Non trié (t)"
                    stackId="tonnage"
                    fill="#d32f2f"
                  />

                  {/* Courbe taux de tri */}
                  <Line
                    yAxisId="rate"
                    type="monotone"
                    dataKey="ratePct"
                    name="Taux de tri (%)"
                    stroke="#000000"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};
